/**
 * POST-INSTALL-01 — the per-install status tracker (Seth 2026-08-20).
 *
 * Four things go to the customer after an install: permit/inspection info,
 * the survey, the referral offer, warranty info. This tracks each as
 * pending / sent / not needed, with two job-level switches:
 *   · permit_required=false — "no permit for this job", decided as early as
 *     the office knows; permit info then never goes out.
 *   · reviews_enabled=false — no survey/review ask for this customer. The
 *     field app gets this switch too (the tech's quiet call after a rough
 *     visit: invoice only, no public-review ask).
 *
 * 'Sent' today = a human sent/handled it (stamped who + when). When the
 * automated journey (Stations 1-3) wires into the comms engine it will set
 * these itself — same row, same statuses. comms_live=false still rules.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { classifyEquipment, parseWarranty } from "@/lib/equipment";

export type PostInstallStatus = "pending" | "sent" | "not_needed";
export type PostInstallItem = "permit" | "survey" | "referral" | "warranty" | "rebate";

export interface PostInstallRow {
  id: string;
  dispatch: string;
  custno: string | null;
  locno: string | null;
  permit_required: boolean;
  reviews_enabled: boolean;
  permit_status: PostInstallStatus;
  permit_sent_at: string | null;
  permit_sent_by: string | null;
  survey_status: PostInstallStatus;
  survey_sent_at: string | null;
  survey_sent_by: string | null;
  referral_status: PostInstallStatus;
  referral_sent_at: string | null;
  referral_sent_by: string | null;
  warranty_status: PostInstallStatus;
  warranty_sent_at: string | null;
  warranty_sent_by: string | null;
  /** PI-05: HOW the warranty registration closed — Seth's tri-state.
   *  'uploaded' = certificate on file + emailed · 'online_no_cert' = registered
   *  on the manufacturer's site, nothing to send · null = n/a or not recorded. */
  warranty_method: "uploaded" | "online_no_cert" | null;
  /** PI-03: utility rebates — submitted by the office; all the customer can
   *  get from us is the confirmation number (updates come from the utility). */
  rebate_status: PostInstallStatus;
  rebate_sent_at: string | null;
  rebate_sent_by: string | null;
  rebate_utility: string | null;
  rebate_confirmation: string | null;
  /** Estimated $ — the utility decides the final number. */
  rebate_amount: number | null;
}

/** Virtual defaults so the panel renders before the first save — the row is
 *  only created when someone actually changes something. */
export const POST_INSTALL_DEFAULTS: Omit<PostInstallRow, "id" | "dispatch" | "custno" | "locno"> = {
  permit_required: true,
  reviews_enabled: true,
  permit_status: "pending",
  permit_sent_at: null,
  permit_sent_by: null,
  survey_status: "pending",
  survey_sent_at: null,
  survey_sent_by: null,
  referral_status: "pending",
  referral_sent_at: null,
  referral_sent_by: null,
  warranty_status: "pending",
  warranty_sent_at: null,
  warranty_sent_by: null,
  warranty_method: null,
  rebate_status: "pending",
  rebate_sent_at: null,
  rebate_sent_by: null,
  rebate_utility: null,
  rebate_confirmation: null,
  rebate_amount: null,
};

/** A tracker row is keyed by the ESC dispatch, the native job id, or BOTH
 *  (a job born from a service call shares one row with the dispatch page). */
export interface PostInstallKey {
  dispatch?: string | null;
  jobId?: string | null;
}

export function usePostInstall(key: PostInstallKey) {
  return useQuery({
    queryKey: ["post_install", key.dispatch ?? null, key.jobId ?? null],
    enabled: !!(key.dispatch || key.jobId),
    queryFn: async (): Promise<PostInstallRow | null> => {
      let q = supabase.from("pegasus_post_install").select("*");
      q = key.dispatch ? q.eq("dispatch", key.dispatch) : q.eq("job_id", key.jobId!);
      const { data, error } = await q.maybeSingle();
      if (error) throw error;
      return (data as PostInstallRow | null) ?? null;
    },
  });
}

/** Upsert-style patch: creates the row on first touch, then updates. */
export function usePatchPostInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      key: PostInstallKey;
      custno: string | null;
      locno: string | null;
      patch: Partial<Omit<PostInstallRow, "id" | "dispatch" | "custno" | "locno">>;
    }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const { error } = await supabase.from("pegasus_post_install").upsert(
        {
          dispatch: v.key.dispatch ?? null,
          job_id: v.key.jobId ?? null,
          custno: v.custno,
          locno: v.locno,
          created_by: who,
          updated_at: new Date().toISOString(),
          ...v.patch,
        },
        { onConflict: v.key.dispatch ? "dispatch" : "job_id" },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post_install"] }),
  });
}

/** Build the patch for a one-item status change, stamping who + when for
 *  'sent'. */
export function itemPatch(item: PostInstallItem, status: PostInstallStatus, who: string | null):
  Record<string, unknown> {
  const p: Record<string, unknown> = { [`${item}_status`]: status };
  p[`${item}_sent_at`] = status === "sent" ? new Date().toISOString() : null;
  p[`${item}_sent_by`] = status === "sent" ? who : null;
  return p;
}

// ---------------------------------------------------------------------------
// PI-05 (Seth 2026-08-25): proposal ↔ post-install rebate link.
//
// The sales tech already picks a utility + estimated rebate on the proposal
// (pegasus_quote_option.rebate_utility_id / rebate_total — it nets off the
// amount due). When the customer ACCEPTS an option, that becomes the promise:
// this hook surfaces it so the post-install card prefills "which company +
// how much was promised". The office can still edit both before sending —
// what's saved on pegasus_post_install is the office's copy of record.
// ---------------------------------------------------------------------------

export interface ProposalRebate {
  utility: string | null;
  amount: number | null;
}

export function useProposalRebate(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["proposal_rebate", jobId ?? null],
    enabled: !!jobId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<ProposalRebate | null> => {
      const { data: q } = await supabase.from("pegasus_quote")
        .select("id, accepted_option_id").eq("job_id", jobId!).maybeSingle();
      const quote = q as { id: string; accepted_option_id: string | null } | null;
      if (!quote?.accepted_option_id) return null; // nothing promised until an option is accepted
      // QUOTE-POLISH-05: the promise is itemized now (CenterPoint for the furnace
      // + Xcel for the A/C) — name every company, sum every amount.
      const { data: rows } = await supabase.from("pegasus_quote_rebate")
        .select("amount, custom_name, utility:pegasus_utility(name)")
        .eq("option_id", quote.accepted_option_id).order("sort");
      const itemized = ((rows ?? []) as unknown) as Array<{ amount: number | null; custom_name: string | null; utility: { name: string } | null }>;
      if (itemized.length) {
        const names = itemized.map((r) => r.utility?.name ?? r.custom_name).filter(Boolean) as string[];
        const sum = itemized.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        if (!names.length && sum <= 0) return null;
        return { utility: names.length ? [...new Set(names)].join(" + ") : null, amount: sum > 0 ? sum : null };
      }
      const { data: o } = await supabase.from("pegasus_quote_option")
        .select("rebate_total, utility:pegasus_utility(name)")
        .eq("id", quote.accepted_option_id).maybeSingle();
      const opt = o as { rebate_total: number | null; utility: { name: string } | null } | null;
      if (!opt) return null;
      const amount = Number(opt.rebate_total) || 0;
      if (!opt.utility?.name && amount <= 0) return null; // no rebate promised on the accepted option
      return { utility: opt.utility?.name ?? null, amount: amount > 0 ? amount : null };
    },
  });
}

// ---------------------------------------------------------------------------
// PI-02 (Seth 2026-08-21): the actual SENDS — through the ONE comms engine.
//
// Queueing here writes a pegasus_outbox row exactly like the automations do:
// comms_live=false -> HELD (visible, never auto-promotes); live -> QUEUED and
// the comms-run cron delivers it within ~10 minutes (Podium text / M365
// email), logging onto the customer's message thread. "Resend" queues a fresh
// row (new dedupe key). Channel policy: survey + referral go TEXT-first
// (email fallback), warranty + permit info go EMAIL-first (text fallback) —
// honoring the customer's no-text / no-email / no-review preferences.
// ---------------------------------------------------------------------------

export type PostInstallSendKey = "post_survey" | "post_referral" | "post_warranty" | "post_permit_info" | "post_rebate";

export const ITEM_SEND_KEY: Record<PostInstallItem, PostInstallSendKey> = {
  survey: "post_survey",
  referral: "post_referral",
  warranty: "post_warranty",
  permit: "post_permit_info",
  rebate: "post_rebate",
};

/** Text-first or email-first, per message type. */
const TEXT_FIRST: Record<PostInstallSendKey, boolean> = {
  post_survey: true,
  post_referral: true,
  post_warranty: false,
  post_permit_info: false,
  post_rebate: false,
};

// RECAP-V2 (Seth 2026-10-02: the recap opened "Hi Rd," — R&D Batteries fed
// through a first-name splitter): greetings are PERSON-aware now. A company
// name never gets split into a fake first name; the LOCATION's person
// ("Randy Noddings" on the Cress View row) outranks the account name; when
// neither is a person, the greeting is a warm plain "there".
const GREET_COMPANY_RE =
  /\b(LLC|INC|CORP|CO|COMPANY|HOMES?|CONSTRUCTION|BUILDERS?|BUILDING|DEVELOP|PROPERTIES|MGMT|MANAGEMENT|APARTMENTS?|REALTY|RENTALS?|GROUP|CHURCH|SCHOOL|BANK|BATTERIES|SERVICES?|ENTERPRISES?)\b/i;

function firstNameOf(name: string | null | undefined): string {
  if (!name) return "there";
  let n = name.trim().replace(/\s+\d{4,}$/, "");
  if (GREET_COMPANY_RE.test(n) || /[&@]/.test(n)) return "there";
  if (n.includes(",")) {
    const i = n.indexOf(",");
    n = `${n.slice(i + 1).trim()} ${n.slice(0, i).trim()}`;
  }
  const first = (n.split(/\s+/)[0] ?? "").replace(/[^A-Za-z'-]/g, "");
  if (first.length < 2) return "there"; // real two-letter names (Ed, Al, Bo) stay
  return first[0].toUpperCase() + first.slice(1).toLowerCase();
}

/** The greeting name: the location's person first, the account second. */
function greetingNameFor(locname: string | null | undefined, fullname: string | null | undefined): string {
  const fromLoc = firstNameOf(locname);
  if (fromLoc !== "there") return fromLoc;
  return firstNameOf(fullname);
}

function fill(s: string, map: Record<string, string>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_m, k) => (k in map ? map[k] : `{{${k}}}`));
}

export interface OutboxTrace {
  id: string;
  automation_key: string;
  channel: string;
  to_address: string | null;
  status: string;
  status_detail: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string;
}

/** Every send attempt for this install, newest first — the "exactly what was
 *  sent and when" read (held / queued / sent / failed / suppressed). */
export function usePostInstallOutbox(key: PostInstallKey) {
  const ref = key.dispatch ?? (key.jobId ? `job-${key.jobId}` : null);
  return useQuery({
    queryKey: ["post_install_outbox", ref],
    enabled: !!ref,
    queryFn: async (): Promise<OutboxTrace[]> => {
      const { data, error } = await supabase
        .from("pegasus_outbox")
        .select("id, automation_key, channel, to_address, status, status_detail, scheduled_for, sent_at, created_at")
        .eq("trigger_kind", "post_install")
        .eq("trigger_ref", ref!)
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as OutboxTrace[];
    },
  });
}

export function useQueuePostInstallSend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      sendKey: PostInstallSendKey;
      key: PostInstallKey;
      custno: string;
      locno: string | null;
      /** reviews_enabled=false strips the review ask from the survey. */
      reviewsEnabled?: boolean;
      /** PI-03 rebate send: the utility + confirmation number for the tokens. */
      rebateUtility?: string | null;
      rebateConfirmation?: string | null;
      /** Estimated rebate dollars (the utility determines the final amount). */
      rebateAmount?: number | null;
      resend?: boolean;
    }): Promise<{ channel: string; to: string; held: boolean }> => {
      const ref = v.key.dispatch ?? `job-${v.key.jobId}`;
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";

      // 1) Template + settings + customer contact points, in parallel.
      const [tplQ, setQ, prefQ, custQ, pegEmailQ, locQ] = await Promise.all([
        supabase.from("pegasus_comms_automation").select("*").eq("key", v.sendKey).maybeSingle(),
        supabase.from("org_setting").select("key, value").in("key", ["comms_live", "survey_link", "feedback_link", "review_link", "referral_terms"]),
        supabase.from("pegasus_comms_pref").select("*").eq("custno", v.custno).maybeSingle(),
        supabase.from("mirror_customer").select("fullname, phone1").eq("custno", v.custno).maybeSingle(),
        supabase.from("pegasus_customer_email").select("email").eq("custno", v.custno).order("created_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("mirror_location").select("locno, locname, add1, city, phone1, email, email2").eq("custno", v.custno).order("locno").limit(10),
      ]);
      const tpl = tplQ.data as {
        template_sms: string | null; template_email_subject: string | null; template_email_body: string | null; enabled: boolean;
      } | null;
      if (!tpl) throw new Error("Template missing — check Settings → Comms Automations.");
      const sv = new Map(((setQ.data ?? []) as { key: string; value: unknown }[]).map((r) => [r.key, r.value]));
      const live = sv.get("comms_live") === true || sv.get("comms_live") === "true";
      const surveyLink = String(sv.get("survey_link") ?? "").trim();
      const feedbackLink = String(sv.get("feedback_link") ?? "").trim();
      const referralTerms = String(sv.get("referral_terms") ?? "").trim() || "Refer a friend: $50 for you, $50 off for them.";
      const pref = prefQ.data as { no_sms: boolean; no_email: boolean; no_review_request: boolean } | null;
      const locs = (locQ.data ?? []) as { locno: string; locname: string | null; add1: string | null; city: string | null; phone1: string | null; email: string | null; email2: string | null }[];
      const loc = (v.locno ? locs.find((l) => l.locno === v.locno) : null) ?? locs[0] ?? null;
      const fullname = (custQ.data as { fullname?: string | null } | null)?.fullname ?? null;
      const phoneRaw = loc?.phone1 ?? (custQ.data as { phone1?: string | null } | null)?.phone1 ?? locs.map((l) => l.phone1).find(Boolean) ?? null;
      const phone10 = String(phoneRaw ?? "").replace(/\D/g, "").slice(-10);
      const email = (
        ((pegEmailQ.data as { email?: string | null } | null)?.email ?? "").trim() ||
        (loc?.email ?? "").trim() || (loc?.email2 ?? "").trim() ||
        (locs.flatMap((l) => [l.email, l.email2]).find((e) => e && e.trim()) ?? "").trim()
      );

      // 2) Channel decision — type default, preference- and data-aware.
      const canSms = phone10.length === 10 && !pref?.no_sms;
      const canEmail = !!email && !pref?.no_email;
      const wantTextFirst = TEXT_FIRST[v.sendKey];
      const channel: "sms" | "email" | null = wantTextFirst
        ? (canSms ? "sms" : canEmail ? "email" : null)
        : (canEmail ? "email" : canSms ? "sms" : null);
      if (!channel) {
        throw new Error(
          pref?.no_sms || pref?.no_email
            ? "This customer's preferences block both channels."
            : "No phone or email on file for this customer.",
        );
      }

      // 3) Fill the template. {{portal_url}} / {{review_link}} stay literal —
      //    the comms engine mints those at SEND time (fresh portal token).
      //    SURVEY FORK (Seth 2026-08-21): reviews ON → the public survey page
      //    (+ the review ask); reviews OFF (the tech's quiet switch, or the
      //    customer's preference) → the PRIVATE feedback page, no review line.
      const reviewAllowed = (v.reviewsEnabled ?? true) && !pref?.no_review_request && String(sv.get("review_link") ?? "").trim() !== "";
      const chosenSurveyLink = reviewAllowed ? surveyLink : (feedbackLink || surveyLink);
      if (v.sendKey === "post_survey" && !chosenSurveyLink) {
        throw new Error("No survey/feedback link set yet — add them in Settings → Post-Install Handoff.");
      }
      const reviewLine = reviewAllowed
        ? (channel === "sms"
            ? "If we earned it, a quick Google review means the world: {{review_link}}"
            : 'And if we earned it, a quick <a href="{{review_link}}">Google review</a> means the world to our team.')
        : "";
      // WARRANTY GATE (Seth: "registered … uploaded post install … then given
      // to customer"): the send links the actual certificate — no cert, no send.
      let warrantyLink = "";
      if (v.sendKey === "post_warranty") {
        let fq = supabase.from("pegasus_file").select("storage_path").eq("custno", v.custno)
          .eq("doc_tag", "warranty_cert").eq("archived", false)
          .order("created_at", { ascending: false }).limit(1);
        fq = v.key.jobId ? fq.eq("job_id", v.key.jobId) : fq.eq("dispatch", v.key.dispatch!);
        const { data: certRow } = await fq.maybeSingle();
        const certPath = (certRow as { storage_path?: string } | null)?.storage_path;
        if (!certPath) throw new Error("Upload the warranty certificate first — the email links the customer straight to it.");
        const { data: signed, error: sErr } = await supabase.storage.from("customer-files")
          .createSignedUrl(certPath, 60 * 60 * 24 * 30);
        if (sErr || !signed?.signedUrl) throw new Error("Couldn't create the certificate link — try again.");
        warrantyLink = signed.signedUrl;
      }
      // REBATE (Seth: "all we can provide is a confirmation number"): the
      // number is the message — require it.
      if (v.sendKey === "post_rebate" && !(v.rebateConfirmation ?? "").trim()) {
        throw new Error("Enter the rebate confirmation number first — it IS the message.");
      }
      // PERMIT-CITY-01: the standalone permit email/text carries the REAL
      // permit numbers (ESC invoice lines) + the city's own instructions —
      // the customer schedules the inspection, never "the inspector will
      // reach out".
      let permitBits = { actionStrip: "", permitBlock: "", citySms: "" };
      if (v.sendKey === "post_permit_info") {
        const pb = await buildPermitBits(v.key, loc?.city ?? null);
        permitBits = { actionStrip: pb.actionStrip, permitBlock: pb.permitBlock, citySms: pb.citySms };
      }
      const tokens: Record<string, string> = {
        first_name: greetingNameFor(loc?.locname, fullname),
        survey_link: chosenSurveyLink,
        referral_terms: referralTerms,
        review_line: reviewLine,
        address: loc?.add1 ?? "your home",
        city: loc?.city ?? "your city",
        action_strip: permitBits.actionStrip,
        permit_block: permitBits.permitBlock,
        city_instructions_sms: permitBits.citySms,
        warranty_link: warrantyLink,
        utility: (v.rebateUtility ?? "").trim() || "your utility company",
        rebate_confirmation: (v.rebateConfirmation ?? "").trim(),
        rebate_amount_line:
          v.sendKey === "post_rebate" && v.rebateAmount != null && v.rebateAmount > 0
            ? (channel === "sms"
                ? ` Estimated amount: ${v.rebateAmount.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })} (final amount determined by the utility).`
                : `<p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 6px;text-align:center">Estimated rebate: <b style="color:#16314f">${v.rebateAmount.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}</b> — the final amount is determined by the utility.</p>`)
            : "",
      };
      const body = channel === "sms"
        ? fill(tpl.template_sms ?? "", tokens).replace(/\s{2,}/g, " ").trim()
        : fill(tpl.template_email_body ?? "", tokens);
      const subject = channel === "email" ? fill(tpl.template_email_subject ?? "Sedgwick Heating", tokens) : null;
      if (!body) throw new Error("The template is empty — check Settings → Comms Automations.");

      // 4) The outbox row — held until comms_live, queued (cron sends) when live.
      const dedupe = `${v.sendKey}:${ref}${v.resend ? `:r${Date.now()}` : ""}`;
      const { error } = await supabase.from("pegasus_outbox").insert({
        automation_key: v.sendKey,
        dedupe_key: dedupe,
        custno: v.custno,
        locno: v.locno,
        customer_name: fullname,
        channel,
        to_address: channel === "sms" ? `+1${phone10}` : email,
        subject,
        body,
        trigger_kind: "post_install",
        trigger_ref: ref,
        scheduled_for: new Date().toISOString(),
        status: live ? "queued" : "held",
        created_by: who,
      });
      if (error) {
        if (/duplicate|unique/i.test(error.message)) throw new Error("Already queued for this install — use Resend to send another copy.");
        throw error;
      }
      return { channel, to: channel === "sms" ? `+1${phone10}` : email, held: !live };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post_install_outbox"] });
      qc.invalidateQueries({ queryKey: ["customer_comms"] });
    },
  });
}

// ---------------------------------------------------------------------------
// INSTALL-RECAP-01 + PERMIT-CITY-01 (Seth 2026-10-01: "I thought we discussed
// if multiple items were done, it would consolidate emails into one recap…
// Right now I was just hit with 5 emails" + "credit river has their own
// instructions. And also we don't schedule on customers behalf").
//
// ONE close-out email, assembled from what Pegasus already knows:
//   · equipment + serials — mirror_equipment rows on the install's ESC invoice
//   · permit numbers — the ESC invoice LINE ITEMS (that's where the office
//     enters them: "Mechanical Permit #: 26-000228" etc.)
//   · the CITY's own inspection instructions — pegasus_city_permit_info
//     (seeded from Seth's Drive letter library; the customer schedules the
//     inspection themselves, never "the inspector will reach out")
//   · warranty — the certificate link when it's uploaded; an honest
//     "certificate will follow" line when it isn't (that send stays separate)
//   · the survey ask + referral offer lines
// Queuing the recap stamps survey/referral/permit (and warranty when the
// cert rode along) as sent, so the separate messages don't ALSO fire.
// ---------------------------------------------------------------------------

const ESC_EQ_ORDER = ["furnace", "air-handler", "boiler", "heat-pump", "rooftop", "magic-pak", "garage-heater", "mini-split", "ac", "coil", "water-heater", "erv-hrv", "humidifier", "dehumidifier", "air-cleaner", "uv", "thermostat", "rds"];

function prettyBrandName(s: string | null | undefined): string {
  const t = (s ?? "").trim();
  if (!t) return "";
  if (t.length <= 3) return t.toUpperCase();
  return t.toLowerCase().split(/\s+/).map((w) => (w.length <= 2 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1))).join(" ");
}

const H2 = (t: string) =>
  `<h2 style="font-size:14px;letter-spacing:0.06em;text-transform:uppercase;color:#16314f;margin:26px 0 10px;border-bottom:1px solid #e8eaed;padding-bottom:6px">${t}</h2>`;
const PARA = (t: string) => `<p style="color:#5f6b7a;font-size:15px;line-height:1.6;margin:0 0 10px">${t}</p>`;
const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** PERMIT-CITY-02 — the permit story, told the way the pre-install letters
 *  tell it (warm, flexible, supportive — never bossy). Mechanical and
 *  electrical are SEPARATE: the mechanical inspection is the customer's one
 *  action item (scheduled directly with the city for maximum flexibility);
 *  the electrical depends on the city — in some (Credit River), Powerhouse
 *  completed it at install and the customer does NOTHING. Permit numbers
 *  come from the ESC invoice line items. Shared by the standalone permit
 *  email AND the close-out recap. */
export async function buildPermitBits(key: PostInstallKey, city: string | null): Promise<{
  actionStrip: string; permitBlock: string; citySms: string; permits: string[]; actionNeeded: boolean;
  /** "December 28, 2026" — install invoice date + the city's window; null when unknown or already passed. */
  deadline: string | null;
}> {
  let invoices: string[] = [];
  let invDate: string | null = null;
  if (key.dispatch) {
    const { data: sales } = await supabase.from("mirror_sales").select("invoice, inv_date").eq("dispatch", key.dispatch).order("inv_date", { ascending: false }).limit(5);
    invoices = [...new Set(((sales ?? []) as { invoice: string }[]).map((s) => s.invoice))];
    invDate = ((sales ?? [])[0] as { inv_date?: string | null } | undefined)?.inv_date ?? null;
  }
  let permits: string[] = [];
  if (invoices.length) {
    const { data: lines } = await supabase.from("mirror_sales_line").select("line_desc, prod").in("invoice", invoices);
    const pseen = new Set<string>();
    permits = ((lines ?? []) as { line_desc: string | null; prod: string | null }[])
      .filter((l) => /permit/i.test(l.line_desc ?? "") || /^PERMIT/i.test(l.prod ?? ""))
      .map((l) => (l.line_desc ?? "").replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length) // keep the most descriptive duplicate
      .filter((d) => {
        const sig = d.replace(/\D/g, "");
        if (!sig || pseen.has(sig)) return false; pseen.add(sig); return true;
      })
      .sort();
  }
  // POST-READY-01 (jobs era): permits ENTERED ON THE JOB (pulled before the
  // install, PERMIT-01 tab) are the authoritative source; the invoice-line
  // scrape above stays as the ESC-era fallback for imported dispatches.
  if (key.jobId) {
    const { data: jp } = await supabase.from("pegasus_permit").select("trade, permit_no").eq("job_id", key.jobId);
    const jobPermits = ((jp ?? []) as { trade: string | null; permit_no: string | null }[])
      .filter((p) => (p.permit_no ?? "").trim() !== "")
      .map((p) => `${(p.trade ?? "").trim() || "Permit"} Permit #: ${(p.permit_no ?? "").trim()}`);
    if (jobPermits.length) permits = jobPermits;
  }

  const cityRaw = (city ?? "").trim();
  const { data: cityRow } = cityRaw
    ? await supabase.from("pegasus_city_permit_info").select("*").eq("city", cityRaw.toLowerCase()).maybeSingle()
    : { data: null };
  const ci = cityRow as {
    display_name: string; inspector_line: string; phone: string | null; window_days: number;
    extra_note: string | null; electrical_mode: "onsite_closed" | "customer_calls" | "unknown";
    electrical_line: string | null; letter_url: string | null;
  } | null;

  // RECAP-V3 (Seth: "more toned down — but also there is a deadline"): the
  // city's window is REAL, so anchor it to a concrete date computed from the
  // install invoice. Warmth and the deadline coexist — the date is firm, the
  // day within it is entirely the customer's. A stale resend after the window
  // has passed falls back to the plain window wording instead of claiming a
  // date that's already gone.
  let deadlineLong: string | null = null;
  let deadlineShort: string | null = null;
  if (ci && invDate) {
    const base = new Date(`${String(invDate).slice(0, 10)}T12:00:00`);
    if (!isNaN(base.getTime())) {
      const d = new Date(base.getTime() + ci.window_days * 86400000);
      if (d.getTime() > Date.now()) {
        deadlineLong = d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
        deadlineShort = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }
    }
  }

  const mech = permits.filter((p) => /mechanical/i.test(p));
  const elec = permits.filter((p) => /electrical/i.test(p));
  const other = permits.filter((p) => !mech.includes(p) && !elec.includes(p));
  const numRow = (p: string) =>
    `<div style="font-size:13.5px;color:#16314f;font-weight:600;padding:2px 0">${escHtml(p)}</div>`;

  // — Mechanical: the one friendly action item, in the letters' own voice. —
  const mechPanel =
    `<div style="border:1px solid #fde4b8;background:#fffaf0;border-radius:11px;padding:14px 16px;margin:0 0 10px">` +
      `<div style="font-size:11px;font-weight:800;letter-spacing:.07em;color:#b45309;margin:0 0 6px">${deadlineLong ? `FOR YOU — BY ${deadlineLong.toUpperCase()}` : ci ? `FOR YOU — WITHIN ${ci.window_days} DAYS` : "FOR YOU, WHEN IT'S CONVENIENT"}</div>` +
      (mech.length ? mech.map(numRow).join("") : "") + (other.length ? other.map(numRow).join("") : "") +
      `<p style="color:#5f6b7a;font-size:14px;line-height:1.6;margin:8px 0 0">` +
      (ci
        ? `${escHtml(ci.display_name)} asks for the final inspection within ${ci.window_days} days of installation${deadlineLong ? ` — for your project, that's any time before <b>${deadlineLong}</b>` : ""}. To give you the most flexibility, you can contact the inspector directly and pick the day that fits your schedule: <b>${escHtml(ci.inspector_line)}</b>${ci.phone ? ` — <a href="tel:${ci.phone.replace(/\D/g, "")}" style="color:#1f5fa8;font-weight:700">${escHtml(ci.phone)}</a>` : ""}. Have your permit number handy.${ci.extra_note && !/permit number/i.test(ci.extra_note) ? ` ${escHtml(ci.extra_note)}` : ""}`
        : `Your city asks for a final inspection after installation, scheduled directly with the city whenever it suits you. Your city's inspection instructions are included with your paperwork — and if anything is unclear, one call to us and we'll walk you through it.`) +
      `</p></div>`;

  // — Electrical: quiet good news when the city lets Powerhouse close it. —
  let elecPanel = "";
  if (elec.length || ci?.electrical_mode === "onsite_closed") {
    const mode = ci?.electrical_mode ?? "unknown";
    const body =
      ci?.electrical_line?.trim() ||
      (mode === "onsite_closed"
        ? `Powerhouse Electric completed the electrical inspection at the time of install — nothing for you to do on that one.`
        : mode === "customer_calls"
          ? `The electrical inspection is scheduled the same easy way — your city's letter has the electrical inspector's contact.`
          : `Your city's letter covers how the electrical inspection is handled — in many cities it was already completed at install.`);
    elecPanel =
      `<div style="border:1px solid #e3e8ee;background:#f8fafc;border-radius:11px;padding:12px 16px;margin:0 0 10px">` +
        `<div style="font-size:11px;font-weight:800;letter-spacing:.07em;color:${mode === "onsite_closed" ? "#15803d" : "#64748b"};margin:0 0 6px">${mode === "onsite_closed" ? "ELECTRICAL — ALREADY TAKEN CARE OF" : "ELECTRICAL"}</div>` +
        (elec.length ? elec.map(numRow).join("") : "") +
        `<p style="color:#5f6b7a;font-size:14px;line-height:1.6;margin:${elec.length ? "8px" : "0"} 0 0">${escHtml(body)}</p>` +
      `</div>`;
  }

  // LETTER-PAGE-01 (Seth: "why are we having to upload these when you
  // already have them"): the letter IS the data — every known city gets its
  // own public page, generated from this very row, so the button works for
  // all 126 cities with nothing to upload. An attached official city PDF
  // (letter_url) is linked FROM that page.
  let letterBtn = "";
  if (ci) {
    const { data: urlRow } = await supabase.from("org_setting").select("value").eq("key", "public_app_url").maybeSingle();
    const base = String((urlRow as { value?: unknown } | null)?.value ?? "https://my.sedgwickheating.com")
      .replace(/^"|"$/g, "").replace(/\/+$/, "");
    const slug = cityRaw.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    letterBtn = `<div style="text-align:center;margin:12px 0 2px"><a href="${base}/letter/${slug}" style="display:inline-block;background:#16314f;color:#fff;text-decoration:none;padding:10px 24px;border-radius:9px;font-weight:700;font-size:13.5px">View your city's full inspection letter</a></div>`;
  }

  const permitBlock = (permits.length || cityRaw)
    ? H2("Permits & your inspection") + mechPanel + elecPanel + letterBtn
    : "";

  const actionNeeded = permits.length > 0 || !!cityRaw;
  const actionStrip = actionNeeded
    ? `<div style="border-left:4px solid #f59e0b;background:#fffbeb;border-radius:0 10px 10px 0;padding:10px 14px;margin:0 0 18px"><p style="color:#92400e;font-size:14px;line-height:1.55;margin:0"><b>One thing for you${deadlineLong ? `, before ${deadlineLong}` : ", when it's convenient"}:</b> your city's final inspection — the day is yours to pick, details just below. Everything else here is simply for your records.</p></div>`
    : "";

  const citySms = ci
    ? `schedule your final inspection any day that suits you — ${ci.inspector_line}${ci.phone ? `, ${ci.phone}` : ""} (within ${ci.window_days} days${deadlineShort ? `, by ${deadlineShort}` : ""}${ci.electrical_mode === "onsite_closed" ? "; the electrical inspection was already completed at install" : ""}).`
    : `schedule your city's final inspection at your convenience — the instructions are with your paperwork, and we're a call away if anything's unclear.`;
  return { actionStrip, permitBlock, citySms, permits, actionNeeded, deadline: deadlineLong };
}

/** The ONE place the recap's recipient is decided — shared by the composer
 *  and the readiness board, so the card can SAY who gets it (POST-READY-01). */
export async function resolveRecapEmail(custno: string): Promise<{ email: string | null; reason: "ok" | "opted_out" | "none" }> {
  const [pegEmailQ, prefQ] = await Promise.all([
    supabase.from("pegasus_customer_email").select("email").eq("custno", custno).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("pegasus_comms_pref").select("no_email").eq("custno", custno).maybeSingle(),
  ]);
  if ((prefQ.data as { no_email?: boolean } | null)?.no_email) return { email: null, reason: "opted_out" };
  let email = (((pegEmailQ.data as { email?: string | null } | null)?.email) ?? "").trim() || null;
  if (!email) {
    const { data: locs } = await supabase.from("mirror_location").select("email, email2").eq("custno", custno).order("locno").limit(10);
    email = (((locs ?? []) as { email: string | null; email2: string | null }[]).flatMap((l) => [l.email, l.email2]).find((e) => e && e.trim()) ?? "").trim() || null;
  }
  return { email, reason: email ? "ok" : "none" };
}

/** POST-READY-01 — the facts the board states BEFORE anything sends:
 *  who the recap goes to, which permit numbers are already on file, the
 *  inspection deadline, whether the certificate is uploaded. */
export interface RecapReadiness {
  toEmail: string | null;
  emailReason: "ok" | "opted_out" | "none";
  permits: string[];
  deadline: string | null;
  cityKnown: boolean;
  electricalClosed: boolean;
  hasEquipment: boolean;
  certOnFile: boolean;
}

export function useRecapReadiness(key: PostInstallKey, custno: string | null, locno: string | null) {
  const ref = key.dispatch ?? (key.jobId ? `job-${key.jobId}` : null);
  return useQuery({
    queryKey: ["recap_readiness", ref, custno, locno],
    enabled: !!ref && !!custno,
    queryFn: async (): Promise<RecapReadiness> => {
      const locQ = locno
        ? supabase.from("mirror_location").select("city").eq("custno", custno!).eq("locno", locno).maybeSingle()
        : supabase.from("mirror_location").select("city").eq("custno", custno!).order("locno").limit(1).maybeSingle();
      const [locRes, mail] = await Promise.all([locQ, resolveRecapEmail(custno!)]);
      const city = ((locRes.data as { city?: string | null } | null)?.city ?? "").trim() || null;
      const bits = await buildPermitBits(key, city);
      const { data: ci } = city
        ? await supabase.from("pegasus_city_permit_info").select("electrical_mode").eq("city", city.toLowerCase()).maybeSingle()
        : { data: null };
      let hasEquipment = false;
      if (key.dispatch) {
        const { data: sales } = await supabase.from("mirror_sales").select("invoice").eq("dispatch", key.dispatch).limit(5);
        const invs = [...new Set(((sales ?? []) as { invoice: string }[]).map((s) => s.invoice))];
        if (invs.length) {
          const { count } = await supabase.from("mirror_equipment").select("counter", { count: "exact", head: true }).in("invoice", invs);
          hasEquipment = (count ?? 0) > 0;
        }
      }
      let fq = supabase.from("pegasus_file").select("id").eq("custno", custno!).eq("doc_tag", "warranty_cert").eq("archived", false).order("created_at", { ascending: false }).limit(1);
      fq = key.jobId ? fq.eq("job_id", key.jobId) : fq.eq("dispatch", key.dispatch!);
      const { data: cert } = await fq.maybeSingle();
      return {
        toEmail: mail.email,
        emailReason: mail.reason,
        permits: bits.permits,
        deadline: bits.deadline,
        cityKnown: !!ci,
        electricalClosed: (ci as { electrical_mode?: string } | null)?.electrical_mode === "onsite_closed",
        hasEquipment,
        certOnFile: !!cert,
      };
    },
  });
}

export interface RecapBuild {
  subject: string;
  html: string;
  toEmail: string | null;
  firstName: string;
  counts: { units: number; permits: number; certIncluded: boolean };
}

/** Assemble the close-out recap for an install ref (ESC dispatch or job). */
export async function buildInstallRecap(
  key: PostInstallKey,
  custno: string,
  locno: string | null,
  reviewsEnabled: boolean,
  permitRequired: boolean = true,
  // FLOW-SIMPLER-01 (Seth: "the office should have ways of turning on and
  // off what they want to send"): an item marked Not Needed on the board
  // genuinely drops out of the recap.
  includeSurvey: boolean = true,
  includeReferral: boolean = true,
): Promise<RecapBuild> {
  const [tplQ, setQ, custQ, locQ, prefQ, mail] = await Promise.all([
    supabase.from("pegasus_comms_automation").select("template_email_subject, template_email_body").eq("key", "post_install_recap").maybeSingle(),
    supabase.from("org_setting").select("key, value").in("key", ["survey_link", "feedback_link", "review_link", "referral_terms"]),
    supabase.from("mirror_customer").select("fullname").eq("custno", custno).maybeSingle(),
    locno ? supabase.from("mirror_location").select("locno, locname, add1, city").eq("custno", custno).eq("locno", locno).maybeSingle()
          : supabase.from("mirror_location").select("locno, locname, add1, city").eq("custno", custno).order("locno").limit(1).maybeSingle(),
    supabase.from("pegasus_comms_pref").select("no_review_request").eq("custno", custno).maybeSingle(),
    resolveRecapEmail(custno),
  ]);
  const tpl = tplQ.data as { template_email_subject: string | null; template_email_body: string | null } | null;
  if (!tpl?.template_email_body) throw new Error("The recap template is missing — check Settings → Comms Automations.");
  const sv = new Map(((setQ.data ?? []) as { key: string; value: unknown }[]).map((r) => [r.key, String(r.value ?? "").replace(/^"|"$/g, "")]));
  const loc = locQ.data as { locno: string; locname: string | null; add1: string | null; city: string | null } | null;
  const fullname = (custQ.data as { fullname?: string | null } | null)?.fullname ?? null;
  const firstName = greetingNameFor(loc?.locname, fullname);

  // Email resolved the ONE shared way (resolveRecapEmail) — same answer the
  // readiness board shows, so what the card SAYS is what the send DOES.
  const toEmail = mail.email;

  // The install's ESC invoice(s) → equipment; permits + city via the shared bits.
  let invoices: string[] = [];
  if (key.dispatch) {
    const { data: sales } = await supabase.from("mirror_sales").select("invoice, inv_date").eq("dispatch", key.dispatch).order("inv_date", { ascending: false }).limit(5);
    invoices = [...new Set(((sales ?? []) as { invoice: string }[]).map((s) => s.invoice))];
  }

  type EqRow = { counter: string; mfg: string | null; model: string | null; serial: string | null; equip_loc: string | null; [k: string]: unknown };
  let units: { label: string; brand: string; model: string; serial: string | null; area: string | null; labor: string | null; parts: string | null }[] = [];
  if (invoices.length) {
    const { data: eq } = await supabase.from("mirror_equipment").select("*").in("invoice", invoices);
    const seen = new Set<string>();
    units = ((eq ?? []) as EqRow[])
      .map((r) => ({ r, c: classifyEquipment(r as never) }))
      .filter(({ c }) => c.tier === "system" || c.tier === "auxiliary")
      .sort((a, b) => {
        const t = (x: { tier: string }) => (x.tier === "system" ? 0 : 1);
        if (t(a.c) !== t(b.c)) return t(a.c) - t(b.c);
        const ia = ESC_EQ_ORDER.indexOf(a.c.category); const ib = ESC_EQ_ORDER.indexOf(b.c.category);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })
      .filter(({ r, c }) => {
        const k = `${c.category}|${(r.model ?? "").toUpperCase()}|${(r.serial ?? "").toUpperCase()}`;
        if (seen.has(k)) return false; seen.add(k); return true;
      })
      .map(({ r, c }) => {
        const w = parseWarranty(r as never);
        const fmt = (d: string | null) => {
          if (!d) return null;
          const t = new Date(d.includes("-") ? `${d}T00:00:00` : d);
          return isNaN(t.getTime()) ? d : t.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        };
        return {
          label: c.categoryLabel,
          brand: prettyBrandName(r.mfg),
          model: r.model?.trim() || "",
          serial: r.serial?.trim() || null,
          area: r.equip_loc?.trim() || null,
          labor: fmt(w.labor),
          parts: fmt(w.parts),
        };
      });
  }

  // "No Permit Needed" on the card drops the inspection story from the recap.
  const bits = permitRequired
    ? await buildPermitBits(key, loc?.city ?? null)
    : { actionStrip: "", permitBlock: "", citySms: "", permits: [] as string[], actionNeeded: false, deadline: null };
  const permits = bits.permits;

  // Warranty certificate — the link only when the file is really there.
  let certUrl: string | null = null;
  {
    let fq = supabase.from("pegasus_file").select("storage_path").eq("custno", custno)
      .eq("doc_tag", "warranty_cert").eq("archived", false)
      .order("created_at", { ascending: false }).limit(1);
    fq = key.jobId ? fq.eq("job_id", key.jobId) : fq.eq("dispatch", key.dispatch!);
    const { data: certRow } = await fq.maybeSingle();
    const certPath = (certRow as { storage_path?: string } | null)?.storage_path;
    if (certPath) {
      const { data: signed } = await supabase.storage.from("customer-files").createSignedUrl(certPath, 60 * 60 * 24 * 30);
      certUrl = signed?.signedUrl ?? null;
    }
  }

  // RECAP-V3 (Seth: "break the equipment down more nicely — model, serial,
  // warranty — more organized"): each unit is a tidy card with a small
  // label/value grid; installs spanning several named areas get area
  // headings, mirroring the 360's Equipment tab.
  const EQ_LBL = `font-size:10px;font-weight:800;letter-spacing:.08em;color:#9aa4b1;padding:2.5px 14px 2.5px 0;vertical-align:top;white-space:nowrap`;
  const EQ_VAL = `font-size:13px;color:#16314f;font-weight:600;padding:2.5px 0;word-break:break-word`;
  const unitCard = (u: (typeof units)[number]) => {
    const rows: string[] = [];
    if (u.model) rows.push(`<tr><td style="${EQ_LBL}">MODEL</td><td style="${EQ_VAL}">${escHtml(u.model)}</td></tr>`);
    if (u.serial) rows.push(`<tr><td style="${EQ_LBL}">SERIAL</td><td style="${EQ_VAL}">${escHtml(u.serial)}</td></tr>`);
    if (u.labor) rows.push(`<tr><td style="${EQ_LBL}">LABOR</td><td style="${EQ_VAL};color:#15803d">Covered through ${escHtml(u.labor)}</td></tr>`);
    if (u.parts) rows.push(`<tr><td style="${EQ_LBL}">PARTS</td><td style="${EQ_VAL};color:#15803d">Covered through ${escHtml(u.parts)}</td></tr>`);
    return (
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e8eaed;border-radius:10px;border-collapse:separate;margin:0 0 8px"><tr><td style="padding:11px 14px">` +
        `<div style="font-size:14.5px;color:#16314f;font-weight:800${rows.length ? ";margin:0 0 5px" : ""}">${escHtml(u.label)}${u.brand ? `<span style="color:#8a94a1;font-weight:600"> · ${escHtml(u.brand)}</span>` : ""}</div>` +
        (rows.length ? `<table role="presentation" cellpadding="0" cellspacing="0">${rows.join("")}</table>` : "") +
      `</td></tr></table>`
    );
  };
  const namedAreas = [...new Set(units.map((u) => u.area).filter((a): a is string => !!a))];
  let equipment_body: string;
  if (namedAreas.length >= 2) {
    const areaHead = (t: string) =>
      `<div style="font-size:11px;font-weight:800;letter-spacing:.07em;color:#64748b;margin:12px 0 6px">${escHtml(t.toUpperCase())}</div>`;
    const groups: [string, typeof units][] = namedAreas.map((a) => [a, units.filter((u) => u.area === a)]);
    const loose = units.filter((u) => !u.area);
    if (loose.length) groups.push(["Additional", loose]);
    equipment_body = groups.map(([a, list]) => areaHead(a) + list.map(unitCard).join("")).join("");
  } else {
    equipment_body = units.map(unitCard).join("");
  }
  const equipment_block = units.length ? H2("What we installed") + equipment_body : "";

  const permit_block = bits.permitBlock;
  const action_strip = bits.actionStrip;

  const warranty_block = H2("Warranty") + (certUrl
    ? PARA(`Your equipment is registered with the manufacturer.`) + `<div style="text-align:center;margin:14px 0"><a href="${certUrl}" style="display:inline-block;background:#c0392b;color:#fff;text-decoration:none;padding:12px 28px;border-radius:9px;font-weight:700;font-size:15px">Download My Certificate</a></div>` + `<p style="color:#9aa4b1;font-size:12.5px;margin:0;text-align:center">This link works for 30 days — misplace it and one call gets you a fresh copy.</p>`
    : PARA(`Your equipment registration is in with the manufacturer. Your warranty certificate will follow in its own email as soon as it's issued — nothing for you to do.`));

  const reviewLink = sv.get("review_link") ?? "";
  const surveyLink = (reviewsEnabled ? sv.get("survey_link") : (sv.get("feedback_link") || sv.get("survey_link"))) ?? "";
  const noReview = (prefQ.data as { no_review_request?: boolean } | null)?.no_review_request ?? false;
  const reviewOk = reviewsEnabled && !noReview && !!reviewLink;
  // RECAP-V2: one light closing strip instead of two more shouting sections.
  // FLOW-SIMPLER-01: each line only rides when its board item is still on.
  const surveyLine = includeSurvey && surveyLink
    ? `<p style="color:#5f6b7a;font-size:13.5px;line-height:1.6;margin:0 0 6px">How did we do? <a href="${surveyLink}" style="color:#1f5fa8;font-weight:700">A two-minute survey</a> genuinely shapes how we work${reviewOk ? ` — and if we earned it, a <a href="${reviewLink}" style="color:#1f5fa8;font-weight:700">Google review</a> means the world to our team` : ""}.</p>`
    : "";
  const referralLine = includeReferral
    ? `<p style="color:#5f6b7a;font-size:13.5px;line-height:1.6;margin:0">${escHtml(sv.get("referral_terms") || "Refer a friend: $50 for you, $50 off for them.")} Just have them mention your name.</p>`
    : "";
  const closing_block = (surveyLine || referralLine)
    ? `<div style="border-top:1px solid #e8eaed;margin-top:24px;padding-top:16px">${surveyLine}${referralLine}</div>`
    : "";

  const tokens: Record<string, string> = {
    first_name: firstName,
    address: [loc?.add1, loc?.city].filter(Boolean).join(", ") || "your home",
    action_strip, equipment_block, permit_block, warranty_block, closing_block,
  };
  const fill = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_m, k) => tokens[k] ?? "");
  return {
    subject: fill(tpl.template_email_subject ?? "Your installation is complete"),
    html: fill(tpl.template_email_body),
    toEmail,
    firstName,
    counts: { units: units.length, permits: permits.length, certIncluded: !!certUrl },
  };
}

/** Queue THE recap to the customer (held while comms are in testing) and
 *  stamp the covered items so the separate sends don't also fire. */
export function useQueueInstallRecap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { key: PostInstallKey; custno: string; locno: string | null; reviewsEnabled: boolean; permitRequired: boolean; includeSurvey: boolean; includeReferral: boolean }) => {
      const ref = v.key.dispatch ?? `job-${v.key.jobId}`;
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const recap = await buildInstallRecap(v.key, v.custno, v.locno, v.reviewsEnabled, v.permitRequired, v.includeSurvey, v.includeReferral);
      if (!recap.toEmail) throw new Error("No email on file for this customer (or they've opted out of email).");
      const { data: gate } = await supabase.from("org_setting").select("value").eq("key", "comms_live").maybeSingle();
      const live = gate?.value === true || gate?.value === "true";
      const { error } = await supabase.from("pegasus_outbox").insert({
        automation_key: "post_install_recap",
        dedupe_key: `post_recap:${ref}`,
        custno: v.custno, locno: v.locno,
        channel: "email", to_address: recap.toEmail,
        subject: recap.subject, body: recap.html,
        trigger_kind: "post_install", trigger_ref: ref,
        scheduled_for: new Date().toISOString(),
        status: live ? "queued" : "held",
        created_by: who,
      });
      if (error) {
        if (/duplicate|unique/i.test(error.message)) throw new Error("A recap is already queued for this install.");
        throw error;
      }
      // The recap covers these — stamp them so nothing double-sends. An item
      // the office switched off (Not Needed) rode nowhere, so it is NOT
      // stamped sent — its not-needed state stays the truth.
      const stamps: Record<string, unknown> = {};
      if (v.includeSurvey) {
        stamps.survey_status = "sent"; stamps.survey_sent_at = new Date().toISOString(); stamps.survey_sent_by = `${who} (recap)`;
      }
      if (v.includeReferral) {
        stamps.referral_status = "sent"; stamps.referral_sent_at = new Date().toISOString(); stamps.referral_sent_by = `${who} (recap)`;
      }
      if (v.permitRequired) {
        stamps.permit_status = "sent"; stamps.permit_sent_at = new Date().toISOString(); stamps.permit_sent_by = `${who} (recap)`;
      }
      if (recap.counts.certIncluded) {
        stamps.warranty_status = "sent"; stamps.warranty_sent_at = new Date().toISOString(); stamps.warranty_sent_by = `${who} (recap)`;
      }
      await supabase.from("pegasus_post_install").upsert(
        { dispatch: v.key.dispatch ?? null, job_id: v.key.jobId ?? null, custno: v.custno, locno: v.locno, created_by: who, updated_at: new Date().toISOString(), ...stamps },
        { onConflict: v.key.dispatch ? "dispatch" : "job_id" },
      );
      return { to: recap.toEmail, held: !live, counts: recap.counts };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post_install"] });
      qc.invalidateQueries({ queryKey: ["post_install_outbox"] });
    },
  });
}

/** The operator test: the SAME recap, real data, straight to MY inbox —
 *  no outbox row, no stamps, can't reach the customer. */
export function useEmailRecapTest() {
  return useMutation({
    mutationFn: async (v: { key: PostInstallKey; custno: string; locno: string | null; reviewsEnabled: boolean; permitRequired: boolean; includeSurvey?: boolean; includeReferral?: boolean; myEmail: string }) => {
      const recap = await buildInstallRecap(v.key, v.custno, v.locno, v.reviewsEnabled, v.permitRequired, v.includeSurvey ?? true, v.includeReferral ?? true);
      const { data, error } = await supabase.functions.invoke("send-email", {
        body: { to: [v.myEmail], subject: `[Pegasus test] ${recap.subject}`, html: recap.html, message_type: "marketing-test" },
      });
      if (error) throw new Error(error.message);
      const err = (data as { error?: string } | null)?.error;
      if (err) throw new Error(err);
      return recap.counts;
    },
  });
}
